import { PerformanceMetric, usePerformance } from '@/context/performance';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useEffect, useRef, useState } from 'react';
import { AppBundle } from '../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import { isBackgroundAppBundlesEnabled } from '@/context/background-app-bundle';

export default function DesignStudio(props?: { basename?: string }) {
  if (isBackgroundAppBundlesEnabled()) {
    return <DesignStudioBackgroundApp basename={props?.basename} />;
  }

  return <DesignStudioNonBackgroundApp basename={props?.basename} />;
}

export function DesignStudioNonBackgroundApp(props?: { basename?: string }) {
  const ref = useRef(null);
  const rendered = useRef(false);
  const root = useRef<any | null>(null);
  const [loaded, setIsLoaded] = useState(false);
  const recordPerformanceMetric = usePerformance();

  useEffect(() => {
    setChildAppLoaded(false);
  }, []);

  useEffect(() => {
    const load = async () => {
      recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
      const { rootComponent: DesignStudioApp, ReactDOMClient: DesignStudioReactDomClient } = (await window.importShim(
        AppBundle.DesignStudio,
      )) as any;
      setIsLoaded(true);

      if (ref.current == null) {
        return;
      }

      if (root.current == null) {
        root.current = DesignStudioReactDomClient.createRoot(ref.current, { identifierPrefix: 'design_studio_' });
      }

      if (!rendered.current && root.current != null) {
        root.current.render(<DesignStudioApp basename={props?.basename} />);
        rendered.current = true;
      }
    };

    load();

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        root.current = null;

        rendered.current = false;
        setIsLoaded(false);
      }
    };
  }, []);

  return (
    <>
      <div ref={ref} style={{ display: loaded ? 'flex' : 'none', minWidth: 0, flex: 1 }}></div>
      {loaded ? null : <PageLoader />}
    </>
  );
}

export function DesignStudioBackgroundApp(props?: { basename?: string }) {
  const rendered = useRef(false);
  const root = useRef<any | null>(null);
  const renderDesignStudioApp = async (appWrapper: HTMLDivElement) => {
    const { rootComponent: DesignStudioApp, ReactDOMClient: DesignStudioReactDomClient } = (await window.importShim(
      AppBundle.DesignStudio,
    )) as any;

    if (root.current == null) {
      root.current = DesignStudioReactDomClient.createRoot(appWrapper, { identifierPrefix: 'design_studio_' });
    }

    if (!rendered.current && root.current != null) {
      root.current.render(<DesignStudioApp basename={props?.basename} />);
      rendered.current = true;
    }

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        root.current = null;
        rendered.current = false;
      }
    };
  };

  return (
    <AppBundleLoader
      appBundle={AppBundle.DesignStudio}
      className="design-studio-app"
      renderApp={renderDesignStudioApp}
    />
  );
}
